const WA_REDIRECT_TIPOS = new Set([6, 20, 24]);

export function isWhatsappRedirectTipo(tipo) {
  return WA_REDIRECT_TIPOS.has(Number(tipo));
}

function asBool(v) {
  return v === true || v === 1 || v === "1";
}

export function channelHasWebchatRedirect(channel, conversation) {
  if (!channel) {
    return asBool(conversation?.webchatRedirectEnabled);
  }
  const tipo = channel.tipo ?? channel.channelTipo ?? channel.typeId;
  if (!isWhatsappRedirectTipo(tipo)) return false;
  if (asBool(channel.webchat_redirect_enabled) || asBool(channel.webchatRedirectEnabled)) {
    return true;
  }
  return asBool(conversation?.webchatRedirectEnabled);
}

export function resolveWebchatPresence(conversation) {
  const p = conversation?.webchatPresence;
  const state = String(p?.state || "").toLowerCase();
  if (state === "online" || state === "away" || state === "offline") {
    return {
      state,
      lastSeenAt: p.lastSeenAt || null,
      canSendWebchat: Boolean(p.canSendWebchat),
      sessionOpen: Boolean(p.sessionOpen),
      redirectEnabled: Boolean(p.redirectEnabled ?? conversation?.webchatRedirectEnabled),
    };
  }
  return {
    state: "offline",
    lastSeenAt: null,
    canSendWebchat: false,
    sessionOpen: asBool(conversation?.webchatAtivo),
    redirectEnabled: asBool(conversation?.webchatRedirectEnabled),
  };
}

const WEBCHAT_ACTIVITY_ONLINE_MS = 90_000;
const WEBCHAT_ACTIVITY_AWAY_MS = 180_000;

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

function messageTimestampMs(message) {
  const raw =
    message?.createdAt ||
    message?.timestamp ||
    message?.data_cadastro ||
    message?.lastUpdate ||
    0;
  const t = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function isClientWebchatMessage(message) {
  const typ = String(message?.type || "").toLowerCase();
  if (typ === "note" || typ === "log" || typ === "xpro") return false;
  const uid = Number(message?.userId ?? message?.id_usuario ?? message?.senderUserId ?? 0);
  if (Number.isFinite(uid) && uid > 0) return false;
  const st = String(message?.senderType || message?.sender || "").toLowerCase();
  if (["agent", "operator", "user", "chatbot", "ai_agent"].includes(st)) return false;
  const j = parseJsonMaybe(message?.json_whatsapp ?? message?.jsonWhatsapp ?? message?.whatsappMeta);
  return Boolean(j && j.xpro_transport === "webchat");
}

export function latestClientWebchatActivityMs(messages) {
  const list = Array.isArray(messages) ? messages : [];
  let latest = 0;
  for (const message of list) {
    if (!isClientWebchatMessage(message)) continue;
    const t = messageTimestampMs(message);
    if (t > latest) latest = t;
  }
  return latest;
}

/** Se o histórico já mostra Web Chat recente, não espera o GET de details. */
export function mergePresenceWithRecentWebchatMessages(presence, messages) {
  const base =
    presence && typeof presence === "object"
      ? presence
      : { state: "offline", canSendWebchat: false };
  const state = String(base.state || "").toLowerCase();
  if (state === "online") return base;
  const at = latestClientWebchatActivityMs(messages);
  if (!at) return base;
  const age = Date.now() - at;
  if (age <= WEBCHAT_ACTIVITY_ONLINE_MS) {
    return { ...base, state: "online", canSendWebchat: true, sessionOpen: true };
  }
  if (age <= WEBCHAT_ACTIVITY_AWAY_MS) {
    return { ...base, state: "away", canSendWebchat: true, sessionOpen: true };
  }
  return base;
}

export function canSendViaWebchat(presence) {
  return presence?.state === "online" || presence?.state === "away" || presence?.canSendWebchat === true;
}

export function defaultWebchatTransport(presence) {
  return canSendViaWebchat(presence) ? "webchat" : "whatsapp";
}

export function isWebchatForeground(presence) {
  return String(presence?.state || "").toLowerCase() === "online";
}

export function isWebchatBackground(presence) {
  return canSendViaWebchat(presence) && !isWebchatForeground(presence);
}
